import { formatApiUrl } from '@ors/helpers'
import { Box } from '@mui/material'

const ProjectsSettingsListsOfTerms = () => {
  const firstColumnClassName = 'py-2 pr-6'
  const secondColumnClassName = 'px-6 py-2'

  return (
    <Box>
      <h2 className="mt-2 text-3xl">
        Management of values for the pick lists used in the IA/BA portal
      </h2>
      <p>
        Although some data can be changed (rows added, edited or deleted) from
        the Django administration area (<a className="italic text-inherit"
        href={formatApiUrl('/admin/')} target="_blank" rel="noopener noreferrer">/admin/</a>),
        only a few types of records can be edited directly using this interface. Those
        types are listed below, together with instructions and caveats.
      </p>
      <p>
        Users who should access the administration area must have the <em>Staff status</em> checked.
        The <em>Superuser status</em> alone does not grant access to the administration area.
      </p>
      <p>
        <strong>Deleting values</strong> should be done with caution, because
        these values may appear in records of related tables. When trying to
        delete a record which has links, the Django administration area will
        present a list of related records and require a confirmation. If there
        are no related records listed, it is safe to delete the value. For
        instance, when attempting to delete a substance, the system will ask for
        the confirmation that the corresponding record from the ODS-ODP table
        will be also deleted:
      </p>
      <img src="/images/projects/delete_confirmation_example.png" className="h-[350px]" />
      <p>
        It is advisable to refrain from deleting records if you don't have a
        clear image of the database and are not aware of the impacts of the
        respective fields and values.
      </p>
      <h3 className="mt-2 text-3xl">
        A) Fields with values configurable from the admin interface.
      </h3>
      <p>
        <strong className="text-[#0095D5]">Agencies:</strong>
        the value "China (FECO)" is excluded from Business Plans. Values "China
        (FECO)", "Treasurer (Cash Pool)" and "Secretariat" are excluded from
        projects.
      </p>

      <table>
        <tr>
          <th className={firstColumnClassName}>Label in application</th>
          <th className={secondColumnClassName}>Values</th>
        </tr>

        <tr>
          <td className={firstColumnClassName}>Agency, Lead agency</td>
          <td className={secondColumnClassName}>
            <a
              className="italic text-inherit"
              target="_blank"
              rel="noopener noreferrer nofollow"
              href={formatApiUrl('/admin/core/agency/')}
            >
              /admin/core/agency/
            </a>
          </td>
        </tr>
      </table>

      <p>
        <strong className="text-[#0095D5]">Alternative technologies: </strong>
        do not edit or remove the value "Other alternatives – specify" . It is
        used to identify when an additional text field should appear.
      </p>

      <table>
        <tr>
          <th className={firstColumnClassName}>Label in application</th>
          <th className={secondColumnClassName}>Values</th>
        </tr>

        <tr>
          <td className={firstColumnClassName}>Replacement technologies</td>
          <td className={secondColumnClassName}>
            <a
              className="italic text-inherit"
              target="_blank"
              rel="noopener noreferrer nofollow"
              href={formatApiUrl('/admin/core/alternativetechnology/')}
            >
              /admin/core/alternativetechnology/
            </a>
          </td>
        </tr>
      </table>

      <p>
        <strong className="text-[#0095D5]">Blends alternative name: </strong>{' '}
        the alternative name of a blend (appears between paranthesis after the
        blend name).
      </p>

      <table>
        <tr>
          <th className={firstColumnClassName}>Label in application</th>
          <th className={secondColumnClassName}>Values</th>
        </tr>

        <tr>
          <td className={firstColumnClassName}>
            Substance - baseline technology
          </td>
          <td className={secondColumnClassName}>
            <a
              className="italic text-inherit"
              target="_blank"
              rel="noopener noreferrer nofollow"
              href={formatApiUrl('/admin/core/blendaltname/')}
            >
              /admin/core/blendaltname/
            </a>
          </td>
        </tr>
      </table>

      <p>
        <strong className="text-[#0095D5]">Blend and blend components: </strong>
        the actual substances that compose a blend. These are also used for
        identifying the group of the blend (for filtering Annex group of
        substances).
      </p>

      <table>
        <tr>
          <th className={firstColumnClassName}>Label in application</th>
          <th className={secondColumnClassName}>Values</th>
        </tr>

        <tr>
          <td className={firstColumnClassName}>
            Substance - baseline technology (blend components)
          </td>
          <td className={secondColumnClassName}>
            <a
              className="italic text-inherit"
              target="_blank"
              rel="noopener noreferrer nofollow"
              href={formatApiUrl('/admin/core/blendcomponents/')}
            >
              /admin/core/blendcomponents/
            </a>
          </td>
        </tr>
        <tr>
          <td className={firstColumnClassName}>
            Substance - baseline technology (blends)
          </td>
          <td className={secondColumnClassName}>
            <a
              className="italic text-inherit"
              target="_blank"
              rel="noopener noreferrer nofollow"
              href={formatApiUrl('/admin/core/blend/')}
            >
              /admin/core/blend/
            </a>
          </td>
        </tr>
      </table>

      <p>
        <strong className="text-[#0095D5]">Countries</strong>
      </p>

      <table>
        <tr>
          <th className={firstColumnClassName}>Label in application</th>
          <th className={secondColumnClassName}>Values</th>
        </tr>

        <tr>
          <td className={firstColumnClassName}>Country</td>
          <td className={secondColumnClassName}>
            <a
              className="italic text-inherit"
              target="_blank"
              rel="noopener noreferrer nofollow"
              href={formatApiUrl('/admin/core/country/')}
            >
              /admin/core/country/
            </a>
          </td>
        </tr>
      </table>

      <p>
        <strong className="text-[#0095D5]">Project specific fields</strong> for
        a given cluster/type/sector combination. In case of indicators, both the
        planned and actual indicator need to be added in the table.
      </p>
      <p>
        <strong>Warning:</strong> do not add any other fields than the specific
        ones, because the cross cutting and identifier ones are defined for
        consistency and implementation purposes. It is best to import the
        combinations from the respective file.
      </p>

      <p>
        These fields can be added from{' '}
        <a
          className="italic text-inherit"
          target="_blank"
          rel="noopener noreferrer nofollow"
          href={formatApiUrl('/admin/core/projectspecificfields/')}
        >
          /admin/core/projectspecificfields/
        </a>
      </p>

      <p>
        <strong>Substances</strong>
      </p>

      <table>
        <tr>
          <th className={firstColumnClassName}>Label in application</th>
          <th className={secondColumnClassName}>Values</th>
        </tr>

        <tr>
          <td className={firstColumnClassName}>Substances</td>
          <td className={secondColumnClassName}>
            <a
              className="italic text-inherit"
              target="_blank"
              rel="noopener noreferrer nofollow"
              href={formatApiUrl('/admin/core/substance/')}
            >
              /admin/core/substance/
            </a>
          </td>
        </tr>

        <tr>
          <td className={firstColumnClassName}>Substance alternative names</td>
          <td className={secondColumnClassName}>
            <a
              className="italic text-inherit"
              target="_blank"
              rel="noopener noreferrer nofollow"
              href={formatApiUrl('/admin/core/substancealtname/')}
            >
              /admin/core/substancealtname/
            </a>
          </td>
        </tr>
        <tr>
          <td className={firstColumnClassName}>Annex group of substances</td>
          <td className={secondColumnClassName}>
            <a
              className="italic text-inherit"
              target="_blank"
              rel="noopener noreferrer nofollow"
              href={formatApiUrl('/admin/core/group/')}
            >
              /admin/core/group/
            </a>
          </td>
        </tr>
      </table>

      <h3 className="mt-2 text-3xl">
        B) Fields that can be changed in the Master Database with a more complex
        process
      </h3>

      <ul>
        <li>
          <strong className="text-[#0095D5]">Cluster</strong>: add values in the
          table{' '}
          <a
            className="italic text-inherit"
            target="_blank"
            rel="noopener noreferrer nofollow"
            href={formatApiUrl('/admin/core/projectcluster/')}
          >
            /admin/core/projectcluster/
          </a>
        </li>
        <li>
          <strong className="text-[#0095D5]">Type</strong>: add values in the
          table{' '}
          <a
            className="italic text-inherit"
            target="_blank"
            rel="noopener noreferrer nofollow"
            href={formatApiUrl('/admin/core/projecttype/')}
          >
            /admin/core/projecttype/
          </a>
        </li>
        <li>
          <strong className="text-[#0095D5]">Sector</strong>: add values in the
          table{' '}
          <a
            className="italic text-inherit"
            target="_blank"
            rel="noopener noreferrer nofollow"
            href={formatApiUrl('/admin/core/projectsector/')}
          >
            /admin/core/projectsector/
          </a>
        </li>
      </ul>
      <p>
        After adding a value from one of the tables above, add as many records as needed to the
        combination of fields in the table
        <a
          className="italic text-inherit"
          target="_blank"
          rel="noopener noreferrer nofollow"
          href={formatApiUrl('/admin/core/projectspecificfields/')}
        >
          /admin/core/projectspecificfields/
        </a> by selecting from the coresponding lists a cluster, a type and a sector, as wel as the 
        combination of project specific fields that should be displayed on the project view/add/edit forms.
        Some of the project specific fields are common for all project types and will be displayed
        even if they are not selected from this list (e.g. country, agency, project dates). 
      </p>
      <img src="/images/projects/project_association_fields.jpg" className="h-[350px]" />


      <h3 className="mt-2 text-3xl">
        C) Fields that cannot be changed in the Master Database
      </h3>

      <p>
        The following fields are defined in the database, but they can only be
        changed by programmers, because of their impact in the application's
        logic:
      </p>
      <ul>
        <li>Project statuses</li>
        <li>Project submission statuses</li>
        <li>Meetings: information automatically pulled from the website</li>
        <li>Decisions: information automatically pulled from the website</li>
        <li>
          Various project fields not mentioned in the first group: some features
          depend on the format or the naming of some of those values. Changing
          the label will not have an effect for other fields than the ones under
          Impact/MYA
        </li>
      </ul>

      <p>
        The following fields do not have the values stored in the Master
        Database; the values are defined in the source code:
      </p>

      <tr>
        <th className={firstColumnClassName}>Label in application</th>
        <th className={secondColumnClassName}>Values</th>
      </tr>
      <tr>
        <td className={firstColumnClassName}>
          Blanket approval or individual consideration
        </td>
        <td className={secondColumnClassName}>
          Blanket
          <br />
          Individual
        </td>
      </tr>
      <tr>
        <td className={firstColumnClassName}>Consumption level status</td>
        <td className={secondColumnClassName}>
          LVC
          <br />
          Non-LVC
          <br />
          N/A
        </td>
      </tr>
      <tr>
        <td className={firstColumnClassName}>Production control type</td>
        <td className={secondColumnClassName}>
          Reduction
          <br />
          Closure
          <br />
          Switch to production for feedstock uses
          <br />
          Conversion to non-controlled substance
          <br />
          Other
        </td>
      </tr>
      <tr>
        <td className={firstColumnClassName}>Category</td>
        <td className={secondColumnClassName}>
          Multi-year agreement
          <br />
          Individual
        </td>
      </tr>
      <tr>
        <td className={firstColumnClassName}>Attachments → Type</td>
        <td className={secondColumnClassName}>
          Project proposal
          <br />
          Endorsement/transmittal letter from government
          <br />
          Verification report
          <br />
          Project review comments
          <br />
          Transferred project proposal
          <br />
          Other
        </td>
      </tr>
    </Box>
  )
}

export default ProjectsSettingsListsOfTerms
